import {AfterViewInit, Component, ElementRef, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {User} from '../../model/user';
import {AuthenticationService} from '../../services/apiServices/security/authentication.service';
import {ActivatedRoute, Router} from '@angular/router';
import {StorageService} from '../../services/storage/storage.service';
import {TranslateService} from '@ngx-translate/core';
import {instances} from '../../properties';
import {environment} from '../../../environments/environment';
import {CommonService} from '../../services/apiServices/business/common.service';
import {ApplicationComponent} from "../application.component";

/**
 * the component display when you have to login the application
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit, AfterViewInit {
  @ViewChild('cid', {static: false}) customerIdFV: ElementRef;
  @ViewChild('uid', {static: false}) userIdFV: ElementRef;
  @ViewChild('upwd', {static: false}) userPasswordFV: ElementRef;
  /**
   * the user to log into the application
   */
  public userToLog: User;

  /**
   * if the user wants to be remembered for future connexion (store idCustomer and idUser into storage)
   */
  public rememberMe: boolean;

  /**
   * if the user last login method was wso
   */
  public hasBeenWsoLoggedIn: boolean;

  /**
   * if the login failed
   */
  public loginFail: boolean;

  /**
   * the message to display in case the login failed
   */
  public errorMessage: string;

  /**
   * if the user wants to display the forgotten password menu
   */
  public displayForgotten: boolean;
  public showCustomerID: boolean;

  /**
   * the url stored if the user was redirected (due to guard) to the login page, useful to redirect after login
   */
  private nextUrl: string;

  /**
   * Toaster Message title on success forgotten password
   */
  private successMessageTitle: string;

  /**
   * Toaster Message content on success forgottent password
   */
  private successMessageContent: string;

  /**
   * Toaster title on success login
   */
  private successLoginTitle: string;

  /**
   * Toaster Message content on success login
   */
  private successLoginMessage: string;

  /**
   * Boolean show dialog redirect wso
   */
  protected showWsoRedirectionDialog = false;

  protected displayLoading: boolean = false;

  /**
   *
   * default constructor that instantiate AuthenticationService and User
   *
   * @param {AuthenticationService} authenticationService - required to login
   * @param {ActivatedRoute} route - required to get queryParams
   * @param {Router} router - required to navigate after login (if needed)
   * @param {StorageService} storageService - usefull to access storage (reload credentials)
   * @param {TranslateService} translate - handleChangeOfSelectFold toaster translation (not it template)
   * @param commonService
   */
  constructor(private authenticationService: AuthenticationService, private route: ActivatedRoute,
              private router: Router, private storageService: StorageService, private translate: TranslateService,
              private commonService: CommonService) {

    this.userToLog = new User();
    this.loginFail = false;
    this.rememberMe = false;
    this.displayForgotten = false;
  }

  /**
   * handleChangeOfSelectFold the subscriptions to the errorMessage and pulling data from storage (if they exist)
   * it also store the url for redirection after login (if necessary)
   * also use the translate service for subscription (only for toaster)
   */
  public ngOnInit(): void {
    this.authenticationService.errorMessage.subscribe(error => {
      this.errorMessage = error;
    });

    if (instances[environment.instance].customerId !== '') {
      this.userToLog.idCustomer = instances[environment.instance].customerId;
      this.showCustomerID = false;
    } else {
      this.showCustomerID = true;
    }

    this.pullCredentialsFromStorage();
    this.getTradForComponent();

    this.nextUrl = this.route.snapshot.queryParams['returnUrl'] || '/';

    this.handleAuthCallback();

    this.route.queryParamMap.subscribe(params => {
      let client = params.get('client');

      if (client == null) {
        client = localStorage.getItem('client') || null;
      }

      localStorage.setItem('client', client);

      if (client !== null && sessionStorage.getItem('AzureLoginAttempt') !== 'true') {
        if (client === 'mbfs' ||client === 'metc' || client == 'brc') {
          this.askWso2Connexion();
          sessionStorage.setItem('AzureLoginAttempt', 'true');
        }
      }
    });
  }


  ngAfterViewInit(): void {
    setTimeout(() => {
      this.userIdFV.nativeElement.focus();
      if (this.showCustomerID) {
        this.customerIdFV.nativeElement.focus();
      }
      this.userPasswordFV.nativeElement.focus();
    }, 300);
  }

  /**
   * handleChangeOfSelectFold connexion and redirection if needed
   */
  public askConnexion(): void {
    this.authenticationService.isWsoRegistered(this.userToLog.idUser, this.userToLog.idCustomer).subscribe(isWso2Registered => {
      if (this.userToLog.idUser === 'adminNumen' || this.userToLog.idUser === 'advNumen' || this.userToLog.idUser === 'SupportNex' || this.userToLog.idUser === 'supportNumen' || this.userToLog.idUser === 'cfeNumen' || !isWso2Registered) {
        this.authenticationService.login(this.userToLog, this.rememberMe, null).subscribe(result => {
          if (!result) {
            this.loginFail = true;
          } else {
            localStorage.setItem('hasBeenWsoLoggedIn', 'false');
            localStorage.setItem('wsoAccessToken', '');
            localStorage.setItem('wsoIdToken', '');
            this.commonService.addToast({
              severity: 'info',
              life: 3000,
              summary: this.successLoginTitle,
              detail: this.successLoginMessage
            });
            this.router.navigateByUrl(this.nextUrl);

          }
        });
      } else {
        this.showWsoRedirectionDialog = true;
      }
    });
  }

  public askWso2Connexion(): void {
    this.authenticationService.redirectToWso2().catch((error) => {
      console.error('Error during WSO2 redirection', error);
      this.loginFail = true;
    });
  }

  private handleAuthCallback(): void {
    this.displayLoading = true;
    this.authenticationService.handleAuthCallback().subscribe((authSuccess) => {
      if (authSuccess) {
        localStorage.setItem('hasBeenWsoLoggedIn', 'true');
        this.commonService.addToast({
          severity: 'info',
          life: 3000,
          summary: this.successLoginTitle,
          detail: this.successLoginMessage
        });
        const client = localStorage.getItem('client') || null;

        this.router.navigateByUrl(this.nextUrl + (client ? `?client=${client}` : ''));

        this.displayLoading = false;

      } else {
        // if(localStorage.getItem('hasBeenWsoLoggedIn') === 'true') {
        //   this.askWso2Connexion()
        // }

        this.loginFail = true;
        this.displayLoading = false;
      }
    });
  }

  public onKeydown(event) {
    if (event.key === 'Enter') {
      if (this.formIsComplete()) {
        this.askConnexion();
      }
    }
  }

  public goToUserId(event) {
    if (event.key === 'Enter') {
      this.userIdFV.nativeElement.focus();
    }
  }

  public goToUserPwd(event) {
    if (event.key === 'Enter') {
      this.userPasswordFV.nativeElement.focus();
    }
  }

  /**
   * obtain translation from TranslateService
   */
  private getTradForComponent(): void {
    this.translate.get('TOASTER.LOGIN_SUCCES_MESSAGE').subscribe((res: string) => {
      this.successMessageTitle = res;
    });
    this.translate.get('TOASTER.SUCESS_REQUEST_OK').subscribe((res: string) => {
      this.successMessageContent = res;
    });
    this.translate.get('TOASTER.SUCCESS_MESSAGE_TITLE').subscribe((res: string) => {
      this.successLoginTitle = res;
    });
    this.translate.get('TOASTER.LOGIN_SUCCES_MESSAGE').subscribe((res: string) => {
      this.successLoginMessage = res;
    });
  }

  /**
   * if credentials are in storage (because the user tick rememberMe) this function use the service to get them back
   */
  private pullCredentialsFromStorage(): void {
    const user = this.storageService.getUserFromStorage();
    if (user.idCustomer && user.idUser) {
      this.userToLog.idCustomer = user.idCustomer;
      this.userToLog.idUser = user.idUser;
      this.userToLog.password = user.password;
      this.rememberMe = true;
    }
  }

  /**
   *
   * check is all the form is valid (allowing user to click on login)
   *
   * @return {boolean} - true if the form is complete, false else
   */
  public formIsComplete(): boolean {
    return !!(this.userToLog.idCustomer && this.userToLog.idUser && this.userToLog.password);
  }

  /**
   * handle display of the GrowlMessage
   *
   * @param {boolean} success - if the operation succeed and the message should be displayed
   */
  public showSuccessToaster(success: boolean): void {
    if (success) {
      this.commonService.addToast({
        life: 3000,
        severity: 'success',
        summary: this.successMessageTitle,
        detail: this.successMessageContent
      });
    }
    this.displayForgotten = !success;
  }
}
